import { type AudienceData, AudienceOption } from '@openx/types/targeting/audience';
import type { TargetingFieldsValues, TargetingReadOnlyProps } from '@openx/types/targeting/targeting';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly';
import { Items } from './Items';
import { ItemsFormPreview } from './ItemsFormPreview';

type AudienceReadOnlyProps = Omit<TargetingReadOnlyProps<AudienceData>, 'field'> & {
  field: TargetingFieldsValues;
  userChangedInitialValue?: boolean;
};

export const AudienceReadOnly = ({
  name,
  field,
  userChangedInitialValue,
  onRemoveClick,
  onOpenDrawerClick,
  targetingParams,
  accountUid,
  readonly,
  isFromCurated,
}: AudienceReadOnlyProps) => {
  const { isDisabled, disabledReasonMessage } = useTargetingDisabledState(field);
  const { t } = useTranslation();

  const optionsCount = useMemo(() => {
    if (!targetingParams) return undefined;

    const keys = Object.keys(targetingParams);
    const hasMatchedUsers = keys.includes(AudienceOption.MATCHED_USERS);
    const hasEids = keys.includes(AudienceOption.TRAFFIC_FILTER_EIDS);

    if (hasMatchedUsers && hasEids) return keys.length - 1;

    return keys.length;
  }, [targetingParams]);

  const hasValue = optionsCount !== undefined && optionsCount > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        isEmpty={!targetingParams}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabled}
        description={t(
          'Select from pre-configured audience groups that combine multiple user characteristics to target specific behavioral or demographic profiles.'
        )}
        isSingleChoice
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        disabledReasonMessage={disabledReasonMessage}
      >
        {targetingParams && (
          <StyledReadOnlyTargetingDropdown>
            <ItemsFormPreview
              targetingParams={targetingParams as unknown as AudienceData}
              accountUid={accountUid as string}
              readonly={readonly}
              isFromCurated={isFromCurated}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              isDisabled={isDisabled}
              skipAlertRender={false}
              optionsCount={optionsCount}
              disabledReasonMessage={disabledReasonMessage}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      isEmpty={!targetingParams}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabled}
      description={t(
        'Select from pre-configured audience groups that combine multiple user characteristics to target specific behavioral or demographic profiles.'
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {targetingParams && (
        <Items
          targetingParams={targetingParams as unknown as AudienceData}
          accountUid={accountUid as string}
          readonly={readonly}
          isFromCurated={isFromCurated}
          skipAlertRender={false}
        />
      )}
    </TargetingReadOnly>
  );
};
